import { useCallback, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { toast } from 'sonner';
import type { Services } from '@/infrastructure/services';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import type { EditableStoryInfo } from '@/modules/subscriptionPlan/types';
import { UNLIMITED } from '@/modules/subscriptionPlan/planConfiguration';
import type { PlanState } from '@/modules/subscriptionPlan/planState';
import { requireReadyPlan } from '@/modules/subscriptionPlan/usePlanState';
import { type BuildDeviceKey, scenePayloadForDevice } from '@/constants/buildDevices';
import { invalidateShareLinks } from '@/hooks/useShareLink';

type StoryStatus = NonNullable<EditableStoryInfo['status']>;

const STATUS_ORDER: StoryStatus[] = ['locked', 'live', 'pending', 'template', 'draft', 'archived'];

export interface StoriesLimitDialogState {
  open: boolean;
  storiesBeingFrozen: Array<{ id: string; name: string; lastUpdated: Date }>;
  maxStories: number;
  pendingScenePayload?: Record<string, unknown>;
}

const CLOSED: StoriesLimitDialogState = { open: false, storiesBeingFrozen: [], maxStories: 0 };

// Last edited is the newest version's time: the story row only changes on a rename or reorder
async function readEditableStories(userId: string): Promise<EditableStoryInfo[]> {
  const [storiesResult, scenesResult] = await Promise.all([
    listAll(page => v1.GET('/api/v1/stories', { params: { query: { userUniqueId: userId, ...page } } })),
    listAll(page => v1.GET('/api/v1/scenes', { params: { query: { userUniqueId: userId, ...page } } })),
  ]);
  if (storiesResult.error) throw storiesResult.error;
  if (scenesResult.error) throw scenesResult.error;

  const versionsByStory = new Map<string, typeof scenesResult.data>();
  for (const scene of scenesResult.data) {
    if (!scene.storyUniqueId) continue;
    const list = versionsByStory.get(scene.storyUniqueId) ?? [];
    list.push(scene);
    versionsByStory.set(scene.storyUniqueId, list);
  }

  return storiesResult.data.map((story) => {
    const versions = versionsByStory.get(story.uniqueId) ?? [];
    const statuses = new Set(versions.map(v => v.status));
    const status = STATUS_ORDER.find(s => statuses.has(s)) ?? 'draft';
    const edited = [story.updatedAt, ...versions.map(v => v.updatedAt)].filter(Boolean).sort();
    return {
      id: story.uniqueId,
      name: story.name || 'Untitled',
      lastUpdated: new Date(edited[edited.length - 1] ?? Date.now()),
      status,
    };
  });
}

interface NewStoryParams {
  userId: string | undefined;
  projectId: string | undefined;
  planState: PlanState;
  services: Services;
  navigate: NavigateFunction;
  reload: () => Promise<void>;
}

export function useNewStory({ userId, projectId, planState, services, navigate, reload }: NewStoryParams) {
  const [isCreatingStory, setIsCreatingStory] = useState(false);
  const [storiesLimitDialog, setStoriesLimitDialog] = useState<StoriesLimitDialogState>(CLOSED);

  const createBlankStory = useCallback(async (device?: BuildDeviceKey) => {
    if (!userId || !projectId || isCreatingStory) return;
    const plan = requireReadyPlan(planState);
    if (!plan) return;
    const scenePayload = device ? scenePayloadForDevice(device) : undefined;

    setIsCreatingStory(true);
    try {
      if (services.subscriptionPlan.getFeatureLimits(plan).maxStories !== UNLIMITED) {
        let stories: EditableStoryInfo[];
        try {
          stories = await readEditableStories(userId);
        } catch (error) {
          console.error('Error reading the stories for the plan limit:', error);
          toast.error("We couldn't check your story limit, so no story was created. Try again.");
          setIsCreatingStory(false);
          return;
        }
        const check = services.subscriptionPlan.canCreateEditableStory(plan, stories);
        if (check.wouldFreezeStories && check.storiesImpacted) {
          setIsCreatingStory(false);
          setStoriesLimitDialog({
            open: true,
            storiesBeingFrozen: check.storiesImpacted,
            maxStories: check.currentLimit || 0,
            pendingScenePayload: scenePayload,
          });
          return;
        }
      }
      await services.scene.createNewScene(userId, navigate, projectId, undefined, scenePayload);
    } catch (error) {
      console.error('Error in handleCreateBlankScene:', error);
      toast.error('Failed to create story');
      setIsCreatingStory(false);
    }
  }, [userId, projectId, isCreatingStory, planState, services, navigate]);

  // Create first, then lock: a failed lock unlocks what this attempt locked and keeps the new story
  const confirmCreateStory = useCallback(async () => {
    if (!userId || !projectId) return;
    const { storiesBeingFrozen, pendingScenePayload } = storiesLimitDialog;
    setIsCreatingStory(true);

    let created: Awaited<ReturnType<Services['scene']['createNewScene']>>;
    try {
      created = await services.scene.createNewScene(userId, navigate, projectId, undefined, pendingScenePayload, { skipNavigate: true });
    } catch (error) {
      console.error('Error creating story:', error);
      toast.error('Failed to create story. No stories were locked.');
      setIsCreatingStory(false);
      return;
    }

    const locked: typeof storiesBeingFrozen = [];
    for (const story of storiesBeingFrozen) {
      const { error } = await toResult(v1.POST('/api/v1/stories/{storyUniqueId}/lock', { params: { path: { storyUniqueId: story.id } } }));
      if (!error) {
        locked.push(story);
        invalidateShareLinks(story.id);
        continue;
      }
      console.error('Error locking story:', error);
      const unlockFailed: string[] = [];
      for (const done of locked) {
        const undo = await toResult(v1.POST('/api/v1/stories/{storyUniqueId}/unlock', { params: { path: { storyUniqueId: done.id } } }));
        if (undo.error) unlockFailed.push(done.name);
      }
      toast.error(
        unlockFailed.length > 0
          ? `Your new story was created, but we couldn't lock "${story.name}" or unlock ${unlockFailed.map(n => `"${n}"`).join(', ')}. Open those stories to check them.`
          : `Your new story was created, but we couldn't lock "${story.name}", so no stories were locked.`,
        { duration: 10000 },
      );
      setIsCreatingStory(false);
      await reload();
      return;
    }

    if (created?.sceneInstanceId) navigate(`/editor/${created.sceneInstanceId}`);
    else {
      setIsCreatingStory(false);
      await reload();
    }
  }, [userId, projectId, storiesLimitDialog, services, navigate, reload]);

  return { isCreatingStory, storiesLimitDialog, setStoriesLimitDialog, createBlankStory, confirmCreateStory };
}
